/**
 * Consolidated Permissions System
 * 
 * This file contains all permission-related functionality in one place:
 * - Composables (usePermissions)
 * - Router Guards (createPermissionGuard, SuperAdminGuard)
 * - Vue Directives (v-permission)
 */

import type { App, DirectiveBinding } from 'vue'
import type { NavigationGuardNext, RouteLocationNormalized } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import JwtService from '@/core/services/JwtService'

// =============================================================================
// TYPES & INTERFACES
// =============================================================================

export type Permission = string | { [key: string]: any; action: string; subject: string };

export interface RouteMeta {
  requiresAuth?: boolean
  permissions?: string[]
  requireAllPermissions?: boolean
  /** When true, only SuperAdmins may access the route (everyone else → /403). */
  superadmin?: boolean
}

interface PermissionBinding extends DirectiveBinding {
  value: string | string[]
  modifiers: {
    all?: boolean
    role?: boolean
  }
}

// =============================================================================
// COMPOSABLES
// =============================================================================

/**
 * Permission utility composable for checking user permissions in the frontend
 * This provides a centralized way to check permissions throughout the application
 */
export function usePermissions() {
  const authStore = useAuthStore()

  /**
   * Check if the current user has a specific permission
   * @param permission - The permission string to check (e.g., 'user:create', 'model:view')
   * @returns boolean
   */
  const hasPermission = (permission: string): boolean => {
    if (!authStore.isAuthenticated || !authStore.user) return false

    // Superadmin has ALL permissions - highest priority check
    if (isSuperAdmin()) return true

    // Check if user has the specific permission from effective_permissions
    const effectivePermissions = authStore.user.effective_permissions || []

    // Handle both string and object permission formats
    return effectivePermissions.some(perm => {
      if (typeof perm === 'string') {
        return perm === permission;
      } else {
        return `${perm.action}:${perm.subject}` === permission;
      }
    })
  }

  /**
   * Check if the current user is a superadmin
   * @returns boolean
   */
  const isSuperAdmin = (): boolean => {
    if (!authStore.isAuthenticated || !authStore.user) return false

    // Use the new superadmin field from user schema
    return Boolean(authStore.user.superadmin)
  }

  /**
   * Get all permissions for the current user
   * @returns string[] - Array of all user permissions
   */
  const getAllPermissions = (): string[] => {
    if (!authStore.isAuthenticated || !authStore.user) return []
    // Superadmin: backend should provide all permissions via effective_permissions
    const effectivePermissions = authStore.user.effective_permissions || []

    // Normalize permissions to strings
    const normalizedPermissions = effectivePermissions.map(perm =>
      typeof perm === 'string' ? perm : `${perm.action}:${perm.subject}`
    )

    return Array.from(new Set(normalizedPermissions))
  }

  return {
    hasPermission,
    isSuperAdmin,
    getAllPermissions,
  }
}

// =============================================================================
// ROUTER GUARDS
// =============================================================================

/**
 * Creates a permission guard for Vue Router
 */
export function createPermissionGuard() {
  return async (
    to: RouteLocationNormalized,
    from: RouteLocationNormalized,
    next: NavigationGuardNext
  ) => {
    const authStore = useAuthStore()
    const meta = to.meta as RouteMeta

    // Skip permission checks for routes that don't require auth
    if (!meta.requiresAuth) {
      next()
      return
    }

    // Must have a token
    if (!JwtService.getToken()) {
      sessionStorage.setItem('redirect_after_login', to.fullPath)
      next('/landing')
      return
    }

    // Ensure auth state is verified once
    if (!authStore.isAuthenticated || !authStore.user?._id) {
      try {
        await (authStore as any).verifyAuth?.()
      } catch {
        // ignore
      }
    }

    // If still not authenticated, redirect
    if (!authStore.isAuthenticated || !authStore.user?._id) {
      sessionStorage.setItem('redirect_after_login', to.fullPath)
      next('/landing')
      return
    }

    const { hasPermission, isSuperAdmin } = usePermissions()

    // Superadmin bypasses all permission checks
    if (isSuperAdmin()) {
      next()
      return
    }

    // SuperAdmin-only routes are off-limits to everyone else
    if (meta.superadmin) {
      next('/403')
      return
    }

    // Check permission requirements
    if (meta.permissions?.length) {
      const ok = meta.requireAllPermissions
        ? meta.permissions.every(p => hasPermission(p))
        : meta.permissions.some(p => hasPermission(p))

      if (!ok) {
        next('/403')
        return
      }
    }

    next()
  }
}

/**
 * Navigation guard specifically for admin routes
 */
export function SuperAdminGuard(
  to: RouteLocationNormalized,
  from: RouteLocationNormalized,
  next: NavigationGuardNext
) {
  const { isSuperAdmin } = usePermissions()
  
  if (!isSuperAdmin()) {
    next('/403')
    return
  }
  
  next()
}

// =============================================================================
// VUE DIRECTIVES
// =============================================================================

/**
 * Check permission for directive
 */
function checkPermissionDirective(el: HTMLElement, binding: PermissionBinding) {
  const { hasPermission, isSuperAdmin } = usePermissions()
  const { value } = binding

  let hasAccess = false

  // Superadmin has access to everything
  if (isSuperAdmin()) {
    hasAccess = true
  } else {
    // Permission-based check
    if (Array.isArray(value)) {
      hasAccess = binding.modifiers.all
        ? value.every(permission => hasPermission(permission))
        : value.some(permission => hasPermission(permission))
    } else {
      hasAccess = hasPermission(value)
    }
  }

  // Show/hide element based on permission
  if (hasAccess) {
    el.style.display = ''
    el.removeAttribute('disabled')
  } else {
    el.style.display = 'none'
  }
}

/**
 * Permission directive for Vue
 */
export const permissionDirective = {
  mounted(el: HTMLElement, binding: PermissionBinding) {
    checkPermissionDirective(el, binding)
  },
  updated(el: HTMLElement, binding: PermissionBinding) {
    checkPermissionDirective(el, binding)
  }
}

/**
 * Setup permission directive in Vue app
 */
export function setupPermissionDirective(app: App) {
  app.directive('permission', permissionDirective)
}

// =============================================================================
// EXPORTS
// =============================================================================

export default {
  usePermissions,
  createPermissionGuard,
  SuperAdminGuard,
  permissionDirective,
  setupPermissionDirective,
}
